import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { storefrontThemeSchema, THEME_FONTS, THEME_KEYS, THEME_RADII } from "./storefront-theme.js";
/**
 * Ready-made business themes (B-85, SF-17): the library the Admin shows, and "Use this theme" — theme, pages,
 * categories, menus and policies in one step, safe to repeat; the previous theme is kept for one-click rollback.
 * Names and descriptions of the themes live in the Admin's i18n; the API sends keys and design tokens.
 */
const tag = { tag: "storefront" };
export const THEME_BUSINESS_TYPES = [
    "fashion",
    "beauty",
    "electronics",
    "food",
    "home",
    "kids",
    "accessories",
    "books",
    "any",
];
export const themeSummarySchema = z.object({
    key: z.enum(THEME_KEYS),
    businessType: z.enum(THEME_BUSINESS_TYPES),
    colors: z.object({ primary: z.string(), accent: z.string() }),
    radius: z.enum(THEME_RADII),
    headingFont: z.enum(THEME_FONTS),
    /** Categories and pages the theme sets up, in the business's language (for the "what you get" list). */
    categories: z.array(z.string()),
    pages: z.array(z.string()),
    /**
     * ST-12: the theme's live demo shop (slug `demo-<key>` on the platform address, seeded in ST-24); null while there
     * is none.
     */
    demoUrl: z.string().nullable().default(null),
    /** PL-07: needs the plan's premium themes. */
    premium: z.boolean().default(false),
});
export const themeLibrarySchema = z.object({
    data: z.array(themeSummarySchema),
    /** The theme last applied with "Use this theme" (null: none yet). */
    applied: z.enum(THEME_KEYS).nullable(),
    /** A previous look is kept and can be restored. */
    canRollback: z.boolean(),
    /** The current theme row version (0 before the first save), for rollback. */
    version: z.int(),
});
export const listThemeLibrary = defineEndpoint({
    ...tag,
    name: "listThemeLibrary",
    method: "GET",
    path: "/v1/storefront-theme-library",
    access: { permission: "storefront.view" },
    summary: "The ready-made business themes",
    status: 200,
    response: themeLibrarySchema,
});
export const applyThemeSchema = z
    .object({
    /** Also overwrite pages that exist already (home, about, contact) with the theme's layout. */
    replacePages: z.boolean().default(false),
    /** Load the sample products too (tagged `sample`; counted against the plan). */
    sampleProducts: z.boolean().default(false),
})
    .strict();
export const appliedThemeSchema = z.object({
    theme: z.enum(THEME_KEYS),
    pagesCreated: z.int(),
    pagesReplaced: z.int(),
    categories: z.int(),
    sampleProducts: z.int(),
    shopUrl: z.string(),
});
export const applyTheme = defineEndpoint({
    ...tag,
    name: "applyTheme",
    method: "POST",
    path: "/v1/storefront-theme-library/{key}/apply",
    access: { permission: "storefront.manage" },
    summary: "Use this theme: theme, pages, categories, menus and policies in one step; repeating it adds nothing",
    params: z.object({ key: z.enum(THEME_KEYS) }),
    body: applyThemeSchema,
    status: 200,
    response: appliedThemeSchema,
    errors: ["FEATURE_NOT_IN_PLAN", "PLAN_LIMIT_REACHED"],
    entitlement: "feature.storefront",
});
export const rollbackTheme = defineEndpoint({
    ...tag,
    name: "rollbackTheme",
    method: "POST",
    path: "/v1/storefront-theme/rollback",
    access: { permission: "storefront.manage" },
    summary: "Restore the look the shop had before the last theme was applied",
    body: z.object({ version: z.int().min(1) }).strict(),
    status: 200,
    response: storefrontThemeSchema,
    errors: ["CONFLICT", "BUSINESS_RULE_VIOLATION"],
});
//# sourceMappingURL=storefront-theme-library.js.map